// Phase H.12a — Slim TipTap instance for the template header & footer.
//
// Why a separate component (not reusing TemplateEditor with a compact prop):
// - TemplateEditor owns auto-save, merge-tags rail, find/replace, status
//   pill — none of that applies to the 1000-char header/footer fields.
// - The header/footer mini-editors need their own ProseMirror state, their
//   own undo stack, and a fixed slim toolbar that includes the Page #
//   insert (which the body editor explicitly hides).
// - Kept small (~150 LOC) on purpose. If we ever need to grow this, we
//   should consolidate with TemplateEditor's toolbar through a shared
//   primitive — not by inheriting the full thing.

import React, { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { TextAlign } from "@tiptap/extension-text-align";
import { Placeholder } from "@tiptap/extension-placeholder";
import {
    Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
    Hash, Layers,
} from "lucide-react";

import { MergeTagNode } from "./MergeTagNode";
import { PageNumberNode, PageNumberTotalNode } from "./PageNumberNode";
import { loadHtmlForEditor, saveHtmlFromEditor } from "./normalize";

const MAX_CHARS = 1000;

function TbBtn({ title, active, onClick, testid, children }) {
    return (
        <button type="button" title={title} aria-label={title} aria-pressed={!!active}
            data-testid={testid}
            onClick={(e) => { e.preventDefault(); onClick && onClick(); }}
            className={`h-7 px-1.5 inline-flex items-center justify-center rounded-md border text-slate-700 hover:bg-slate-100 ${active ? "bg-blue-50 border-blue-300 text-blue-900" : "border-transparent hover:border-slate-200"} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}>
            {children}
        </button>
    );
}

export default function MiniEditor({
    mode = "header",        // "header" | "footer" (only affects testids + placeholder)
    initialHtml = "",
    onChange,               // (html: string) => void — fires on every doc update
    onMaxLengthExceeded,    // optional: notified when char count > MAX_CHARS
}) {
    const lastEmittedRef = useRef("");

    const editor = useEditor({
        immediatelyRender: false,
        extensions: [
            StarterKit.configure({
                heading: false,
                bulletList: false,
                orderedList: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
                code: false,
            }),
            Underline,
            TextStyle,
            TextAlign.configure({ types: ["paragraph"] }),
            Placeholder.configure({
                placeholder: mode === "footer"
                    ? "Footer text — appears at the bottom of every PDF page."
                    : "Header text — appears at the top of every PDF page.",
            }),
            MergeTagNode,
            PageNumberNode,
            PageNumberTotalNode,
        ],
        content: loadHtmlForEditor(initialHtml || ""),
    });

    // Push changes upward as plain HTML (chips normalized back to `{{tag}}`).
    useEffect(() => {
        if (!editor) return;
        const tick = () => {
            const html = saveHtmlFromEditor(editor.getHTML());
            if (html === lastEmittedRef.current) return;
            lastEmittedRef.current = html;
            // Soft length guard — the backend rejects > 1000 chars with 422
            // and we surface it visually below.
            if (html.length > MAX_CHARS && onMaxLengthExceeded) onMaxLengthExceeded(html.length);
            onChange && onChange(html);
        };
        editor.on("update", tick);
        return () => { editor.off("update", tick); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    if (!editor) return null;

    const ed = editor;
    const currentHtml = saveHtmlFromEditor(ed.getHTML());
    const overLimit = currentHtml.length > MAX_CHARS;

    return (
        <div className="border border-slate-200 rounded-md bg-white" data-testid={`mini-editor-${mode}`}>
            <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 bg-slate-50 rounded-t-md flex-wrap">
                <TbBtn title="Bold" testid={`mini-${mode}-bold`} active={ed.isActive("bold")}
                    onClick={() => ed.chain().focus().toggleBold().run()}>
                    <Bold size={14} />
                </TbBtn>
                <TbBtn title="Italic" testid={`mini-${mode}-italic`} active={ed.isActive("italic")}
                    onClick={() => ed.chain().focus().toggleItalic().run()}>
                    <Italic size={14} />
                </TbBtn>
                <TbBtn title="Underline" testid={`mini-${mode}-underline`} active={ed.isActive("underline")}
                    onClick={() => ed.chain().focus().toggleUnderline().run()}>
                    <UnderlineIcon size={14} />
                </TbBtn>
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <TbBtn title="Align left" testid={`mini-${mode}-align-left`} active={ed.isActive({ textAlign: "left" })}
                    onClick={() => ed.chain().focus().setTextAlign("left").run()}>
                    <AlignLeft size={14} />
                </TbBtn>
                <TbBtn title="Align center" testid={`mini-${mode}-align-center`} active={ed.isActive({ textAlign: "center" })}
                    onClick={() => ed.chain().focus().setTextAlign("center").run()}>
                    <AlignCenter size={14} />
                </TbBtn>
                <TbBtn title="Align right" testid={`mini-${mode}-align-right`} active={ed.isActive({ textAlign: "right" })}
                    onClick={() => ed.chain().focus().setTextAlign("right").run()}>
                    <AlignRight size={14} />
                </TbBtn>
                <span className="w-px h-5 bg-slate-200 mx-1" />
                <TbBtn title="Insert page number" testid={`mini-${mode}-page-number`}
                    onClick={() => ed.chain().focus().insertPageNumber().run()}>
                    <Hash size={14} />
                </TbBtn>
                <TbBtn title="Insert “Page X of Y”" testid={`mini-${mode}-page-of-total`}
                    onClick={() => ed.chain().focus().insertPageOfTotal().run()}>
                    <Layers size={14} />
                </TbBtn>
            </div>
            <EditorContent
                editor={editor}
                data-testid={`mini-editor-${mode}-content`}
                className="prose prose-sm max-w-none px-3 py-2 min-h-[60px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px] [&_.page-number]:inline-flex [&_.page-number]:items-center [&_.page-number]:px-1.5 [&_.page-number]:py-0.5 [&_.page-number]:bg-violet-50 [&_.page-number]:text-violet-900 [&_.page-number]:rounded [&_.page-number]:border [&_.page-number]:border-violet-200 [&_.page-number]:text-xs [&_.page-number-total]:inline-flex [&_.page-number-total]:items-center [&_.page-number-total]:px-1.5 [&_.page-number-total]:py-0.5 [&_.page-number-total]:bg-violet-50 [&_.page-number-total]:text-violet-900 [&_.page-number-total]:rounded [&_.page-number-total]:border [&_.page-number-total]:border-violet-200 [&_.page-number-total]:text-xs"
            />
            <div className="flex items-center justify-between px-2 py-1 border-t border-slate-200 text-[10px] text-slate-500 bg-slate-50 rounded-b-md">
                <span data-testid={`mini-${mode}-char-count`}>{currentHtml.length} / {MAX_CHARS} chars</span>
                {overLimit && (
                    <span data-testid={`mini-${mode}-over-limit`} className="text-red-600 font-medium">
                        Over the {MAX_CHARS}-char limit — save will be rejected
                    </span>
                )}
            </div>
        </div>
    );
}
